import type { CampaignAnalyticsGranularity } from "@adpilot/shared";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { fetchCampaignAnalytics } from "@/lib/dashboard-api";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { fetchSalesTraffic } from "@/lib/sales-traffic-api";

export interface DashboardPerformanceScope {
  workspaceId: string | null;
  shopIds: string[];
  startDate: string;
  endDate: string;
  timeZone: ReportingTimeZone;
  granularity: CampaignAnalyticsGranularity;
  /** Child ASIN filter; empty or omitted means the whole shop scope. */
  asins?: string[];
}

/** Pin both sources to one date snapshot, including when a request crosses midnight. */
export function dashboardPerformanceQueries(scope: DashboardPerformanceScope) {
  const shopIds = [...scope.shopIds].sort();
  const asins = [...(scope.asins ?? [])].sort();
  const range = { type: "custom" as const, startDate: scope.startDate, endDate: scope.endDate };
  const key = [
    scope.workspaceId,
    shopIds.join(","),
    scope.startDate,
    scope.endDate,
    scope.timeZone,
    asins.join(","),
  ];
  return {
    sales: queryOptions({
      queryKey: ["dashboard", "sales-traffic", ...key],
      queryFn: ({ signal }) =>
        fetchSalesTraffic({ range, timeZone: scope.timeZone, shopIds, asins }, signal),
      enabled: shopIds.length > 0,
      // Never use the previous key's data: a changed shop/date is a new scope.
      staleTime: 0,
      refetchInterval: (query) => {
        const data = query.state.data;
        if (
          data?.shops.some(
            (shop) =>
              shop.latestSync?.status === "queued" || shop.latestSync?.status === "processing",
          )
        )
          return 10_000;
        return data?.connected ? 60_000 : false;
      },
      refetchIntervalInBackground: false,
    }),
    ads: queryOptions({
      queryKey: ["dashboard", "campaign-analytics", ...key, scope.granularity],
      queryFn: ({ signal }) =>
        fetchCampaignAnalytics(
          {
            range,
            timeZone: scope.timeZone,
            shopIds,
            asins,
            granularity: scope.granularity,
            delivery: "all",
            search: "",
            policy: [],
            run: [],
          },
          signal,
        ),
      enabled: shopIds.length > 0,
    }),
  };
}

export function useDashboardPerformance(scope: DashboardPerformanceScope) {
  const { t } = useTranslation("dashboard");
  const options = dashboardPerformanceQueries(scope);
  const sales = useQuery(options.sales);
  const ads = useQuery(options.ads);
  useEffect(() => {
    // HTTP 401/403 are already handled by the client. Transport failures get
    // one toast; the chart's inline Sales notice offers retry for every error.
    if (
      sales.isError &&
      sales.errorUpdatedAt > 0 &&
      (sales.error instanceof TypeError || sales.error.name === "TimeoutError")
    ) {
      toast.error(t("salesTraffic.loadFailed"));
    }
  }, [sales.error, sales.errorUpdatedAt, sales.isError, t]);
  return { sales, ads };
}
